import { useEffect, useRef, useState } from "react";
import { useNavigate, useRouterState, useSearch } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BIO_MAX, MAX_CLUB_GENRES, type Me } from "@pagemates/shared";
import { api, describeError } from "../lib/api";
import { authClient } from "../lib/auth";
import { safeNext } from "../lib/returnTo";
import { Avatar, Spinner } from "../components/ui";
import { CheckIcon, CloseIcon } from "../components/Icons";
import { GenreChips } from "../components/ClubHome";
import { shrink } from "../components/AvatarPicker";
import { Card } from "../components/LibraryCard";
import { useMe } from "../lib/queries";
import { onboarding } from "@pagemates/shared/copy";

const txt = onboarding.welcome;

/*
 * First sign-in: issuing your card (PLAN.md §Profiles as library cards).
 * Sets how you'll appear to clubmates before anyone sees you, and ends with
 * your library card. Same shell as starting a club. Runs once, for accounts
 * created after it shipped; closing it partway keeps what was saved and it
 * doesn't come back. From an invite, it hands you back to that invite.
 */

export type WelcomeSearch = { step?: number; next?: string; hardcover?: string };

/** Sends a signed-in account that hasn't had the flow to it, remembering where it was going. */
export function useWelcomeGate() {
  const session = authClient.useSession();
  const me = useMe({ enabled: Boolean(session.data) });
  const navigate = useNavigate();
  const href = useRouterState({ select: (r) => r.location.href });
  const path = useRouterState({ select: (r) => r.location.pathname });
  useEffect(() => {
    if (me.data?.needsWelcome && path !== "/welcome") void navigate({ to: "/welcome", search: { next: href }, replace: true });
  }, [me.data?.needsWelcome, path]); // eslint-disable-line react-hooks/exhaustive-deps
}


export function WelcomePage() {
  const me = useMe();
  if (me.isPending) return <main className="page"><Spinner /></main>;
  if (!me.data) return <main className="page"><p className="error">{describeError(me.error)}</p></main>;
  return <Flow me={me.data} />;
}

function Flow({ me }: { me: Me }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const search = useSearch({ from: "/authed/welcome" });
  const withHardcover = me.hardcover.available;
  const steps = withHardcover ? (["you", "about", "hardcover", "card"] as const) : (["you", "about", "card"] as const);
  const asked = search.step && search.step >= 1 ? Math.min(search.step, 4) : 1;
  const key = asked === 4 ? "card" : steps[Math.min(asked, steps.length) - 1]!;
  const idx = steps.indexOf(key as never);
  const go = (k: (typeof steps)[number]) => void navigate({ to: "/welcome", search: { ...search, step: k === "card" ? 4 : steps.indexOf(k as never) + 1, hardcover: undefined } });
  const nextStep = () => go(steps[idx + 1]!);

  const done = useMutation({
    mutationFn: api.welcomed,
    onSuccess: async () => { await qc.invalidateQueries({ queryKey: ["me"] }); void navigate({ to: safeNext(search.next) as "/clubs", replace: true }); },
  });

  // step 1
  const [name, setName] = useState(me.displayName ?? me.name);
  // step 2
  const [bio, setBio] = useState("");
  const [genres, setGenres] = useState<string[]>([]);
  const save = useMutation({ mutationFn: api.updateProfile, onSuccess: () => qc.invalidateQueries({ queryKey: ["me"] }) });

  const submit = async () => {
    if (key === "you") {
      const n = name.trim();
      if (n !== (me.displayName ?? me.name)) await save.mutateAsync({ displayName: n && n !== me.name ? n : null });
      nextStep();
    } else if (key === "about") {
      if (bio.trim() || genres.length) await save.mutateAsync({ bio: bio.trim() || null, genres });
      nextStep();
    } else if (key === "hardcover") nextStep();
    else done.mutate();
  };

  const shown = name.trim() || me.name;
  return (
    <main className="page newclub welcome">
      <div className="ob card">
        <div className="ob-top">
          <button type="button" className="pencil" aria-label={txt.close} onClick={() => done.mutate()}><CloseIcon width={20} height={20} /></button>
          <span className="muted small">{txt.yourCard}</span>
          <span style={{ width: 32 }} />
        </div>
        <form className="ob-form" noValidate onSubmit={(e) => { e.preventDefault(); void submit(); }}>
          <div className="ob-scroll">
            <div className="ob-dots" aria-label={txt.stepOf(idx + 1, steps.length)}>{steps.map((k, i) => <i key={k} className={i === idx ? "on" : i < idx ? "done" : ""} />)}</div>
            <div className="ob-grid solo">
              <div className="ob-left">
                {key === "you" && <YouStep me={me} name={name} setName={setName} shown={shown} />}
                {key === "about" && (
                  <>
                    <h1 className="ob-t">{txt.aLittleAboutYou}</h1>
                    <p className="ob-a">{txt.bothOptionalTheyShow}</p>
                    <div className="field"><label htmlFor="w-bio">{txt.oneLineAboutYou}</label>
                      <input id="w-bio" className="input" value={bio} maxLength={BIO_MAX} placeholder={txt.slowReaderFastTalker} onChange={(e) => setBio(e.target.value)} />
                      <span className="muted small" style={{ textAlign: "right" }}>{bio.length} / {BIO_MAX}</span>
                    </div>
                    <div className="field"><label>{txt.favouriteGenres}<span className="muted small">{txt.upTo(MAX_CLUB_GENRES)}</span></label>
                      <GenreChips value={genres} onChange={setGenres} />
                    </div>
                  </>
                )}
                {key === "hardcover" && (
                  <>
                    <h1 className="ob-t">{txt.useHardcover}</h1>
                    <p className="ob-a">{txt.yourProgressUpdatesItself}</p>
                    {search.hardcover === "linked" || me.hardcover.linked
                      ? <p className="ok"><CheckIcon width={14} height={14} />{txt.linkedAs}{me.hardcover.username}.</p>
                      : <p className="muted small">{txt.pagematesOnlyAsksTo}</p>}
                    {search.hardcover === "denied" && <p className="muted small">{txt.nothingWasLinkedYou}</p>}
                    {search.hardcover === "error" && <p className="error small">{txt.thatDidnTGo}</p>}
                  </>
                )}
                {key === "card" && (
                  <div className="w-issued">
                    <h1 className="ob-t">{txt.yourCardIsIssued}</h1>
                    <p className="ob-a">{txt.everyClubBookYou}</p>
                    <div className="w-slot" aria-hidden="true" />
                    <Card name={shown} no={1} issuedAt={me.createdAt} stamps={[]} rows={6} className="issuing" />
                  </div>
                )}
                {save.isError && <p className="error small">{describeError(save.error)}</p>}
              </div>
            </div>
          </div>
          <div className="ob-foot">
            {idx > 0 && key !== "card" ? <button type="button" className="btn ghost" onClick={() => window.history.back()}>{txt.back}</button> : <span />}
            <div className="row" style={{ gap: 8 }}>
              {key === "about" && <button type="button" className="btn" onClick={nextStep}>{txt.skip}</button>}
              {key === "hardcover" && !me.hardcover.linked && search.hardcover !== "linked" ? (
                <>
                  <button type="button" className="btn" onClick={nextStep}>{txt.notNow}</button>
                  <a className="btn primary" href="/api/hardcover/connect?from=welcome">{txt.linkHardcover}</a>
                </>
              ) : (
                <button type="submit" className="btn primary" disabled={save.isPending || done.isPending || (key === "you" && !name.trim())}>{key === "card" ? txt.continue : txt.next}</button>
              )}
            </div>
          </div>
        </form>
      </div>
    </main>
  );
}

/** Step 1: the name, the picture (a sign-in's, an upload, or initials) and how it looks in Talk. */
function YouStep({ me, name, setName, shown }: { me: Me; name: string; setName: (v: string) => void; shown: string }) {
  const qc = useQueryClient();
  const choices = useQuery({ queryKey: ["avatar-choices"], queryFn: api.avatarChoices, staleTime: 5 * 60_000 });
  const file = useRef<HTMLInputElement>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ["me"] });
  const pick = useMutation({ mutationFn: api.useProviderPicture, onSuccess: refresh });
  const initials = useMutation({ mutationFn: api.useInitials, onSuccess: refresh });
  const up = useMutation({ mutationFn: async (f: File) => api.uploadAvatar(await shrink(f)), onSuccess: refresh });
  const busy = pick.isPending || initials.isPending || up.isPending;
  const list = choices.data ?? [];
  return (
    <>
      <h1 className="ob-t">{txt.howClubmatesWillSee}</h1>
      <p className="ob-a">{txt.yourNameAndPhoto}</p>
      <div className="field"><label htmlFor="w-name">{txt.name}</label>
        <input id="w-name" className="input" value={name} maxLength={40} autoFocus autoComplete="name" onChange={(e) => setName(e.target.value)} /></div>
      <div className="field"><label>{txt.photo}</label>
        <div className="w-pics" role="radiogroup" aria-label={txt.photo}>
          {list.map((c) => (
            <button key={c.provider} type="button" role="radio" aria-checked={me.image === c.url} className={`w-pic${me.image === c.url ? " on" : ""}`} disabled={busy} onClick={() => pick.mutate(c.provider)} aria-label={txt.yourPicture(c.provider)}>
              <img src={c.url} alt="" />
            </button>
          ))}
          {me.avatarSource === "upload" && me.image && (
            <button type="button" role="radio" aria-checked className="w-pic on" aria-label={txt.yourUploadedPhoto}><img src={me.image} alt="" /></button>
          )}
          <button type="button" role="radio" aria-checked={!me.image} className={`w-pic${!me.image ? " on" : ""}`} disabled={busy} onClick={() => initials.mutate()} aria-label={txt.initials}>
            <Avatar name={shown} image={null} />
          </button>
          <button type="button" className="w-pic up" disabled={busy} onClick={() => file.current?.click()} aria-label={txt.uploadAPhoto}>{up.isPending ? "…" : "+"}</button>
          <input ref={file} type="file" accept="image/*" hidden onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) up.mutate(f); }} />
        </div>
        {(pick.isError || initials.isError || up.isError) && <p className="error small">{describeError(pick.error ?? initials.error ?? up.error)}</p>}
      </div>
      <div className="w-preview" aria-label={txt.howYouLlLook}>
        <div className="msg">
          <Avatar name={shown} image={me.image} />
          <div style={{ minWidth: 0 }}>
            <div className="who"><b>{shown}</b><span className="muted small">{txt.justNow}</span></div>
            <div className="body">{txt.chapter3HadMe}</div>
          </div>
        </div>
      </div>
    </>
  );
}
